import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Button } from 'primeng/button';
import { Checkbox } from 'primeng/checkbox';
import { TableModule } from 'primeng/table';
import { ProgressBar } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { Divider } from 'primeng/divider';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { SvgIconComponent } from 'angular-svg-icon';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { GridLinesDatatableToken } from 'src/theme/components/datatable/grid-lines-datatable-token';
import { ICorrelationTableData } from '@app/main/leak-detection/interfaces/correlation-table-data';
import { LeakDetectionTableCorrelationsDataService } from '@app/main/leak-detection/services/leak-detection-table-correlations/leak-detection-table-correlations-data.service';
import { LdCorrelationTypeIconComponent } from '@app/main/leak-detection/components/ld-helper/ld-correlation-type-icon/ld-correlation-type-icon.component';
import { getCommonStateStatusCssColor } from '@app/shared/utils/common-state-status-css-color';
import { FormattingDataService } from '@app/core/services/formatting-data/formatting-data.service';
import { ILocalizationKey } from '@app/core/interfaces/localization';
import { ReportCoordinatesOption } from '@app/core/enums/report-coordinates-option.enum';
import { LD_REPORT_COORDINATES_OPTIONS } from '@app/main/leak-detection/configs/ld-report-coordinates.config';
import { reportCoordinatesLocalize } from '@app/core/localize/report-coordinates';
import { CORRELATION_REPORT_PARAMETERS_CHECKBOX_OPTIONS } from '@app/main/leak-detection/dialogs/dialog-create-correlation-report/dialog-create-correlation-report.config';
import { CorrelationReportParametersControlKey } from '@app/main/leak-detection/enums/correlation-report-parameters-control-key.enum';
import { ReportUrlService } from '@app/main/main-screen/services/report-url/report-url.service';
import { LdReportType } from '@app/main/leak-detection/enums/ld-report-type.enum';
import { ReportRowSelectionState, ReportTableSortState } from '@app/main/leak-detection/utils/report-table-state';
import { SortOrder } from '@app/core/enums/sort-order.enum';

enum CorrelationReportTab {
  Correlations = 'correlations',
  Parameters = 'parameters',
}

type CorrelationSortField = 'quality' | 'logger1' | 'logger2';

interface ICorrelationReportParametersForm extends Record<CorrelationReportParametersControlKey, FormControl<boolean>> {
  correlationIds: FormControl<string>;
  coordinates: FormControl<ReportCoordinatesOption>;
  additionalInformation: FormControl<string>;
}

export interface IDialogCreateCorrelationReportData {
  correlationIds?: string[];
}

@Component({
  selector: 'gc-dialog-create-correlation-report',
  templateUrl: './dialog-create-correlation-report.component.html',
  styleUrl: './dialog-create-correlation-report.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgTemplateOutlet,
    Button,
    Checkbox,
    TableModule,
    ProgressBar,
    SelectModule,
    TextareaModule,
    Divider,
    FormsModule,
    ReactiveFormsModule,
    SvgIconComponent,
    TranslateDirective,
    TranslatePipe,
    LdCorrelationTypeIconComponent,
  ],
})
export class DialogCreateCorrelationReportComponent {
  private ref = inject(DynamicDialogRef);
  private config = inject<DynamicDialogConfig<IDialogCreateCorrelationReportData>>(DynamicDialogConfig);
  private dataService = inject(LeakDetectionTableCorrelationsDataService);
  private reportUrlService = inject(ReportUrlService);
  protected readonly formattingData = inject(FormattingDataService);

  public readonly gridLinesDatatableToken = GridLinesDatatableToken;
  public readonly getCommonStateStatusCssColor = getCommonStateStatusCssColor;
  protected readonly CorrelationReportTab = CorrelationReportTab;
  protected readonly SortOrder = SortOrder;

  public readonly parametersCheckboxOptions = CORRELATION_REPORT_PARAMETERS_CHECKBOX_OPTIONS;
  public readonly coordinatesOptions = LD_REPORT_COORDINATES_OPTIONS;
  public readonly coordinatesLabels = reportCoordinatesLocalize;

  public parametersForm = new FormGroup<ICorrelationReportParametersForm>({
    [CorrelationReportParametersControlKey.Map]: new FormControl(true, { nonNullable: true }),
    [CorrelationReportParametersControlKey.StreetView]: new FormControl(true, { nonNullable: true }),
    [CorrelationReportParametersControlKey.CorrelationGraph]: new FormControl(true, { nonNullable: true }),
    [CorrelationReportParametersControlKey.CorrelationSpectrum]: new FormControl(true, { nonNullable: true }),
    [CorrelationReportParametersControlKey.SpectrumOfBothLoggers]: new FormControl(true, { nonNullable: true }),
    [CorrelationReportParametersControlKey.NoiseLevelHistory]: new FormControl(true, { nonNullable: true }),
    correlationIds: new FormControl('', { nonNullable: true, validators: Validators.required }),
    coordinates: new FormControl(ReportCoordinatesOption.None, { nonNullable: true }),
    additionalInformation: new FormControl('', { nonNullable: true }),
  });

  public activeTab = signal<CorrelationReportTab>(CorrelationReportTab.Correlations);

  public correlations = signal<ICorrelationTableData[]>([]);

  private tableSort = new ReportTableSortState<ICorrelationTableData, CorrelationSortField>(
    'quality',
    SortOrder.Desc,
    (row, field) => {
      switch (field) {
        case 'quality':
          return row.quality ?? 0;
        case 'logger1':
          return row.logger1SerialNo ?? '';
        case 'logger2':
          return row.logger2SerialNo ?? '';
      }
    },
    {
      quality: ['logger1', 'logger2'],
      logger1: ['logger2'],
    }
  );
  public sortField = this.tableSort.sortField;
  public sortOrder = this.tableSort.sortOrder;
  public sortedCorrelations = computed(() => this.tableSort.sort(this.correlations()));

  private rowSelection = new ReportRowSelectionState<ICorrelationTableData>(() => this.correlations());
  public selectedIds = this.rowSelection.selectedIds;
  public totalCount = this.rowSelection.totalCount;
  public selectedCount = this.rowSelection.selectedCount;
  public allSelected = this.rowSelection.allSelected;
  public partiallySelected = this.rowSelection.partiallySelected;

  constructor() {
    this.dataService
      .getData()
      .pipe(take(1), takeUntilDestroyed())
      .subscribe(rows => {
        this.correlations.set(rows);

        const preselectedIds = (this.config.data?.correlationIds ?? []).filter(id => rows.some(row => row.id === id));

        if (preselectedIds.length) {
          this.rowSelection.selectRows(preselectedIds);
        } else {
          this.rowSelection.selectAllRows();
        }
      });

    effect(() => {
      this.parametersForm.controls.correlationIds.setValue(this.rowSelection.selectedRowIds().join(','));
    });
  }

  public setActiveTab(tab: CorrelationReportTab): void {
    this.activeTab.set(tab);
  }

  public toggleSort(field: CorrelationSortField): void {
    this.tableSort.toggleSort(field);
  }

  public toggleAll(): void {
    this.rowSelection.toggleAll();
  }

  public toggleRow(id: string): void {
    this.rowSelection.toggleRow(id);
  }

  public coordinatesLabel(value: ReportCoordinatesOption): ILocalizationKey {
    return this.coordinatesLabels[value];
  }

  public isPreviewDisabled(): boolean {
    return (
      this.parametersForm.invalid ||
      this.parametersCheckboxOptions.every(option => !this.parametersForm.controls[option.controlKey].value)
    );
  }

  public cancel(): void {
    this.ref.close();
  }

  public preview(): void {
    const value = this.parametersForm.getRawValue();

    this.reportUrlService
      .openWithCurrentSelection({
        type: LdReportType.Correlation,
        additionalInfo: value.additionalInformation,
        coordinateFormat: value.coordinates,
        ids: value.correlationIds,
        includeMap: value.map,
        includeStreetView: value.streetView,
        includeCorrelationGraph: value.correlationGraph,
        includeCorrelationSpectrum: value.correlationSpectrum,
        includeLoggerSpectra: value.spectrumOfBothLoggers,
        includeLoggerDbMinHistories: value.noiseLevelHistory,
      })
      .subscribe(opened => {
        if (opened) this.ref.close();
      });
  }
}
